import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIngredientPricesQuery, useOrderDetailsMutation } from '../../features/ordersApiSlice';
import { useParams } from 'react-router';
import {
  CONSTANTS,
  EXTRA_UNITS,
  kcal_per_g_carbs,
  kj_per_g_carbs,
  MICRO_INGREDIENT_IDS,
} from '../../Data/OrdersPdf';
import { ORDER_TRANSLATIONS } from '../../translations/ordersPdf';
import { declaredDaysForRiiseDuration, pouchWeightMg } from '../../utils/labelDays';
import LabelIconsImg from '../../assets/images/pdf/label-ingredients-4x6.png';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Micro Ingredients Id and name
// export const MICRO_INGREDIENT_IDS = [25, 18, 20, 26, 28, 30, 33, 34, 37];
// 25 Vitamin B7
// 18 Vitamin A
// 20 Vitamin B12
// 26 Vitamin B9
// 28 Vitamin D3
// 30 Vitamin K
// 33 Vitamin B9 (L-MTHF)
// 34 Vitamin B12 (Methylcobalamin)
// 37 Iodine (from Fucus) — added SHI-554; see the note in Data/OrdersPdf

const LabelIngredients4x6 = () => {
  const { id } = useParams();
  const pageRef = useRef();

  const [order, setOrder] = useState({});
  const [lang, setLang] = useState('de');
  const orderDetailsRef = useRef();

  const getOrderData = async (row) => {
    try {
      const res = await getOrderDetails({ order_id: id }).unwrap();
      setLang(res?.shippingAddress?.[0]?.country === 'DE' ? 'de' : 'en');
      setOrder(res);
    } catch (e) {
      console.log('Error => ', e);
    }
  };

  useEffect(() => {
    getOrderData();
  }, [id]);

  const [getOrderDetails, { isLoading }] = useOrderDetailsMutation();
  const { data: ingredientPrices } = useGetIngredientPricesQuery(lang);
  console.log('ingredientPrices => ', ingredientPrices);

  const TRANSlATIONS = useMemo(() => ORDER_TRANSLATIONS[lang], [lang]);

  const downloadPdf = async (refs, orderId) => {
    // 4x6 inches in mm: 4 inches = 101.6mm, 6 inches = 152.4mm
    const pdf = new jsPDF('p', 'mm', [98.5, 149]);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < refs.length; i++) {
      const canvas = await html2canvas(refs[i].current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      // Calculate dimensions to fit the 4x6 page
      const imgProps = {
        width: pageWidth,
        height: (canvas.height * pageWidth) / canvas.width,
      };

      // If height exceeds page height, scale down to fit
      if (imgProps.height > pageHeight) {
        imgProps.height = pageHeight;
        imgProps.width = (canvas.width * pageHeight) / canvas.height;
      }

      if (i !== 0) pdf.addPage([101.6, 152.4]);
      pdf.addImage(imgData, 'JPEG', 0, 0, imgProps.width, imgProps.height);
    }

    pdf.save(`riise_label_4x6_${orderId}.pdf`);
  };

  const totalWeight = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const prices = ingredientPrices?.data?.find((item) => item?.ingredient_id === element?.ingredient_id);
      const active = parseFloat(prices?.active_percentage);

      const hydroSum = (parseFloat(element?.daily_dose) * parseFloat(prices?.hydro_percentage)) / active;
      const saccharoseSum =
        (parseFloat(element?.daily_dose) * parseFloat(prices?.saccharose_percentage)) / active;
      const starchSum = (parseFloat(element?.daily_dose) * parseFloat(prices?.starch_percentage)) / active;
      const restSum =
        (parseFloat(element?.daily_dose) * parseFloat(prices?.rest_ingredients_percentage)) / active;

      const allSum = parseFloat(element?.daily_dose) + hydroSum + saccharoseSum + starchSum + restSum;

      sum += allSum;
    });
    // SHI-528 — declared supply (30/month), not the produced count (31/month), and built
    // from the FILLED pouch: neutral pellets are in the bag and consumed.
    // See utils/labelDays; the old form mis-read the riise plan code as a month count.
    const total = pouchWeightMg(sum) * declaredDaysForRiiseDuration(order?.data?.[0]?.duration);
    return Math.round(total / 1000);
  }, [order, ingredientPrices]);

  const bestBeforeDate = useMemo(() => {
    const originalDate = new Date(order?.data?.[0]?.date);
    const newDate = new Date(originalDate);
    newDate.setFullYear(originalDate.getFullYear() + 2);
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0'); // months are 0-based
    const day = String(newDate.getDate()).padStart(2, '0');
    return `${day}.${month}.${year}`;
  }, [order]);

  const totalSaccharose = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find((item) => item?.ingredient_id === element?.ingredient_id);
      if (price) {
        const sumOfPrices =
          (parseFloat(price?.saccharose_percentage) * parseFloat(element?.daily_dose)) /
          parseFloat(price?.active_percentage);
        sum += sumOfPrices;
      }
    });
    return sum;
  }, [order]);

  const totalStarch = useMemo(() => {
    let sum = 0;
    order?.ingredients?.forEach((element) => {
      const price = ingredientPrices?.data?.find((item) => item?.ingredient_id === element?.ingredient_id);
      if (price) {
        sum +=
          (parseFloat(price?.starch_percentage) * parseFloat(element?.daily_dose)) /
          parseFloat(price?.active_percentage);
      }
    });
    return sum;
  }, [order]);

  const carbohydrate = useMemo(
    () => Math.round((totalSaccharose + totalStarch) / 1000),
    [totalSaccharose, totalStarch],
  );

  const carbohydratePercentage = useMemo(() => {
    const percentage = carbohydrate / CONSTANTS['Carbohydrates'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [carbohydrate]);

  const sugar = useMemo(() => Math.round(totalSaccharose / 1000), [totalSaccharose]);

  const sugarPercentage = useMemo(() => {
    const percentage = sugar / CONSTANTS['Sugars'];
    return percentage < 1 ? '<1%' : percentage.toFixed(2) + '%';
  }, [sugar]);

  const energy = useMemo(
    () => kcal_per_g_carbs * carbohydrate + '/' + kj_per_g_carbs * carbohydrate,
    [carbohydrate],
  );

  const energyPercentage = useMemo(() => {
    const energyPerc = (kcal_per_g_carbs * carbohydrate) / CONSTANTS['Enerygy'];
    return energyPerc < 1 ? '<1%' : energyPerc.toFixed(2) + '%';
  }, [carbohydrate]);

  const ingredients = useMemo(
    () =>
      ingredientPrices?.data?.filter((item) =>
        order?.ingredients?.some((orderIng) => orderIng.ingredient_id === item.ingredient_id),
      ) || [],
    [ingredientPrices?.data, order?.ingredients],
  );

  const ingredientLabelString = useMemo(
    () =>
      ingredients
        ?.map((item) => `${item?.ingredient?.ingredient} (${item?.ingredient?.scientific_name})`)
        .filter(Boolean)
        .join(', '),
    [ingredients],
  );

  console.log('ingredients => ', ingredients);
  console.log('ingredientLabelString => ', ingredientLabelString);

  const renderIngredientsSection = () => {
    const halfLength = Math.ceil((order?.ingredients?.length || 0) / 2) + 2;
    const leftColumn = order?.ingredients?.slice(0, halfLength) || [];
    const rightColumn = order?.ingredients?.slice(halfLength) || [];

    const renderIngredientLine = (item) => {
      const dose = MICRO_INGREDIENT_IDS?.includes(item?.ingredient_id)
        ? (parseFloat(item?.daily_dose) * 1000).toFixed(2)
        : item?.daily_dose;
      const unit = MICRO_INGREDIENT_IDS?.includes(item?.ingredient_id) ? ' µg' : ' mg';
      const extraUnit = Object.keys(EXTRA_UNITS)?.includes(item?.ingredient_id?.toString())
        ? ' ' + EXTRA_UNITS[item?.ingredient_id]
        : '';
      const nrv = item?.per_eu_nrv === 0 ? '*' : item?.per_eu_nrv + '%';

      return (
        <div key={item.id} className="t-flex t-items-end t-justify-between">
          <div className="t-flex-1">
            <span className="t-text-[20px] t-font-semibold" style={{ lineHeight: '20px' }}>
              {item?.ingredient}
            </span>
          </div>

          <div className="t-flex t-items-center t-justify-center t-gap-2">
            <span
              className="t-min-w-[80px] t-text-[20px] t-font-semibold t-text-right"
              style={{ lineHeight: '20px' }}
            >
              {dose}
              {unit}
              {extraUnit}
            </span>
            <span
              className="t-min-w-[70px] t-text-right t-text-[20px] t-font-semibold"
              style={{ lineHeight: '20px' }}
            >
              {nrv}
            </span>
          </div>
        </div>
      );
    };

    const renderColumnHeader = () => (
      <div className="t-border-t t-border-b t-border-black t-py-2 mb-2">
        <h3 className="t-text-xl t-font-bold t-m-0">{TRANSlATIONS.nutritionalValues}</h3>
        <p className="t-text-xl t-font-semibold t-m-0">{TRANSlATIONS.nutrientAmountNrv}</p>
      </div>
    );

    return (
      <div className="t-border-b t-border-black t-pb-4">
        <div className="t-flex t-gap-10">
          <div className="t-flex-1">
            {renderColumnHeader()}
            <div className="t-font-mono t-text-xs t-leading-relaxed">
              {leftColumn.map((item) => renderIngredientLine(item))}
            </div>
          </div>
          <div className="t-flex-1">
            {renderColumnHeader()}
            <div className="t-font-mono t-text-xs t-leading-relaxed">
              {rightColumn.map((item) => renderIngredientLine(item))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderNutritionTable = () => {
    const nutritionData = [
      { label: TRANSlATIONS.energy, value: `${energy} kcal/kj`, percentage: energyPercentage },
      { label: TRANSlATIONS.fat, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.saturatedFattyAcids, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.carbohydrates, value: `${carbohydrate} g`, percentage: carbohydratePercentage },
      { label: TRANSlATIONS.sugar, value: `${sugar} g`, percentage: sugarPercentage },
      { label: TRANSlATIONS.protein, value: '0 g', percentage: '0%' },
      { label: TRANSlATIONS.salt, value: '0 g', percentage: '0%' },
    ];

    return (
      <div className="t-flex t-gap-6 t-items-start t-justify-between">
        {/* Left side - Icons */}
        <div className="t-flex-shrink-0 t-mt-12">
          <img src={LabelIconsImg} alt="Label Icons" className="t-w-40" />
        </div>

        {/* Right side - Nutrition Table */}
        <div className="t-flex-1 t-max-w-[350px] t-border-b t-border-black t-pb-4">
          <div className="t-border-b t-border-black t-py-2">
            <h3 className="t-text-xl t-font-bold t-m-0 t-text-right">{TRANSlATIONS.nutritionalInfo}</h3>
          </div>
          <div className="t-font-mono t-text-xs t-leading-relaxed t-text-lg">
            {nutritionData.map((item, index) => (
              <div key={index} className="t-flex t-items-center ">
                <p
                  className="t-flex-1 t-text-[20px] t-font-semibold t-leading-0"
                  style={{ lineHeight: '20px' }}
                >
                  {item.label}
                </p>
                <p
                  className="t-flex t-items-center t-justify-between t-gap-2 t-text-[20px] t-font-semibold"
                  style={{ lineHeight: '20px' }}
                >
                  <span className="t-min-w-[100px] t-text-center">{item.value}</span>
                  <span className="t-min-w-[50px] t-text-right">{item.percentage}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  return (
    <>
      <button onClick={() => downloadPdf([pageRef], order?.data?.[0]?.id)}>Download</button>
      <div className="t-w-[794px] t-mx-auto t-flex t-flex-col t-bg-white" ref={orderDetailsRef}>
        <div className="t-relative t-flex t-flex-col t-gap-1 t-p-3" ref={pageRef}>
          <div className="t-flex t-items-center t-justify-between t-font-bold">
            <p className="t-text-lg">Riise for you - {order?.data?.[0]?.first_name}</p>
            <p className="t-text-lg">
              {TRANSlATIONS.netQuantity}: {totalWeight ?? 0}g
            </p>
          </div>
          <p className="t-text-[20px] t-font-semibold t-pb-4" style={{ lineHeight: '20px' }}>
            {TRANSlATIONS.ingredients}: {ingredientLabelString}, {TRANSlATIONS.ingredientsDesc}
          </p>
          {renderIngredientsSection()}
          {renderNutritionTable()}
          <div className="t-flex t-items-center t-justify-between t-font-bold">
            <p className="t-text-lg">
              {TRANSlATIONS.batchNumber}: RIISE-{order?.data?.[0]?.id}
            </p>
            <p className="t-text-lg">
              {TRANSlATIONS.bestBeforeDate}: {bestBeforeDate}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LabelIngredients4x6;
